import { TenantResolutionError } from './errors';
import { isLocalHostname, normalizeHostname } from './hostname';
import { validateTenantId } from './paths';
import { TenantRuntime } from './types';

type HostnameResolver = (hostname: string) => Promise<TenantRuntime>;

export type ResolveTenantRuntimeOptions = {
  explicitTenant?: TenantRuntime;
  hostname?: string;
  search?: string;
  legacyTenantId?: string;
  hostnameResolutionEnabled: boolean;
  localOverrideAllowed: boolean;
  controlPlaneOverrideAllowed?: boolean;
  resolveHostname?: HostnameResolver;
};

const validateRuntime = (runtime: TenantRuntime): TenantRuntime => ({
  ...runtime,
  tenantId: validateTenantId(runtime.tenantId),
  hostname: runtime.hostname ? normalizeHostname(runtime.hostname) : undefined,
});

export const resolveTenantRuntime = async (
  options: ResolveTenantRuntimeOptions
): Promise<TenantRuntime> => {
  if (options.explicitTenant) {
    return validateRuntime(options.explicitTenant);
  }

  const params = new URLSearchParams(options.search ?? '');
  if (params.has('tenant')) {
    if (!options.localOverrideAllowed && !options.controlPlaneOverrideAllowed) {
      throw new TenantResolutionError(
        'LOCAL_OVERRIDE_NOT_ALLOWED',
        'Tenant query overrides are only allowed in the local environment.'
      );
    }
    return { tenantId: validateTenantId(params.get('tenant') ?? '') };
  }

  if (options.hostnameResolutionEnabled && options.hostname) {
    const hostname = normalizeHostname(options.hostname);
    if (!isLocalHostname(hostname)) {
      if (!options.resolveHostname) {
        throw new TenantResolutionError('MISSING_TENANT', 'Hostname resolution is not configured.');
      }
      // When host resolution is enabled, lookup failures are final. Never fall
      // back to the legacy build tenant for an unknown production hostname.
      return validateRuntime(await options.resolveHostname(hostname));
    }
  }

  if (options.legacyTenantId) {
    return { tenantId: validateTenantId(options.legacyTenantId) };
  }

  throw new TenantResolutionError('MISSING_TENANT', 'No tenant is configured for this application.');
};
